import { useState, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Sparkles, Mail, Lock, ArrowRight, Loader2, Eye, EyeOff, ShieldCheck, AlertCircle } from 'lucide-react';
import { GoogleAuthButton } from '../components/GoogleAuthButton';
import { toast } from 'sonner';

const RAW_BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const BACKEND_URL = (!RAW_BACKEND_URL || RAW_BACKEND_URL === 'undefined') ? '' : RAW_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const SSOLoginPage = () => {
  const [params] = useSearchParams();
  const { user, login, verify2fa, logout } = useAuth();

  const clientId = params.get('client_id') || '';
  const redirectUri = params.get('redirect_uri') || '';
  const state = params.get('state') || '';
  const scope = params.get('scope') || 'openid profile email';
  const codeChallenge = params.get('code_challenge') || '';
  const codeChallengeMethod = params.get('code_challenge_method') || '';

  const [clientMeta, setClientMeta] = useState(null);
  const [validating, setValidating] = useState(true);
  const [error, setError] = useState('');

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [redirecting, setRedirecting] = useState(false);

  // Inline 2FA state
  const [mfa, setMfa] = useState(null); // { mfa_token, method, masked_email }
  const [otp, setOtp] = useState('');

  // 1) Validate the authorization request + fetch client metadata
  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!clientId || !redirectUri) {
        setError('This sign-in link is missing required parameters.');
        setValidating(false);
        return;
      }
      try {
        const qs = new URLSearchParams({
          client_id: clientId,
          redirect_uri: redirectUri,
          response_type: 'code',
          scope,
          ...(state ? { state } : {}),
          ...(codeChallenge ? { code_challenge: codeChallenge } : {}),
          ...(codeChallengeMethod ? { code_challenge_method: codeChallengeMethod } : {}),
        }).toString();
        const { data } = await axios.get(`${API}/sso/authorize?${qs}`);
        if (!cancelled) setClientMeta(data.client);
      } catch (e) {
        if (!cancelled) setError(e.response?.data?.detail || 'This application is not authorized for SSO.');
      } finally {
        if (!cancelled) setValidating(false);
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clientId, redirectUri]);

  // 2) Grant consent → mint code → redirect back to the requesting app
  const grantConsentAndRedirect = useCallback(async () => {
    setRedirecting(true);
    try {
      const token = localStorage.getItem('token');
      const { data } = await axios.post(
        `${API}/sso/authorize/consent`,
        {
          client_id: clientId,
          redirect_uri: redirectUri,
          scope,
          state: state || null,
          code_challenge: codeChallenge || null,
          code_challenge_method: codeChallengeMethod || null,
        },
        { headers: token ? { Authorization: `Bearer ${token}` } : {} },
      );
      window.location.href = data.redirect_url;
    } catch (e) {
      setRedirecting(false);
      setError(e.response?.data?.detail || 'Could not complete sign-in. Please try again.');
    }
  }, [clientId, redirectUri, scope, state, codeChallenge, codeChallengeMethod]);

  const handleLogin = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const result = await login(email, password);
      if (result?.requires_2fa) {
        setMfa({ mfa_token: result.mfa_token, method: result.twofa_method, masked_email: result.masked_email });
        toast.message('One more step', { description: 'Enter your verification code' });
        return;
      }
      await grantConsentAndRedirect();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Sign in failed');
    } finally {
      setSubmitting(false);
    }
  };

  const handleVerify2fa = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await verify2fa(mfa.mfa_token, otp);
      await grantConsentAndRedirect();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Invalid code');
    } finally {
      setSubmitting(false);
    }
  };

  const appName = clientMeta?.name || 'the application';

  if (validating) {
    return (
      <div className="min-h-screen bg-zinc-50 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-indigo-600" />
      </div>
    );
  }

  if (error && !clientMeta) {
    return (
      <div className="min-h-screen bg-zinc-50 flex items-center justify-center p-4" data-testid="sso-error">
        <Card className="w-full max-w-md border-zinc-200 shadow-sm">
          <CardContent className="pt-8 pb-8 text-center space-y-3">
            <div className="w-12 h-12 rounded-full bg-red-50 flex items-center justify-center mx-auto">
              <AlertCircle className="w-6 h-6 text-red-500" />
            </div>
            <h1 className="text-xl font-bold text-zinc-900">Can't sign in</h1>
            <p className="text-sm text-zinc-500" data-testid="sso-error-message">{error}</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-zinc-50 flex items-center justify-center p-4" data-testid="sso-login-page">
      <div className="w-full max-w-md">
        <div className="text-center mb-8 animate-fade-in">
          <div className="inline-flex items-center gap-2 mb-6">
            <div className="w-10 h-10 rounded-xl bg-indigo-600 flex items-center justify-center">
              <Sparkles className="w-5 h-5 text-white" />
            </div>
            <span className="text-xl font-bold tracking-tight">PostToSocial</span>
          </div>
          <div className="flex items-center justify-center gap-2 text-zinc-500 text-sm mb-2">
            <ShieldCheck className="w-4 h-4 text-emerald-500" />
            <span>Secure single sign-on</span>
          </div>
          <h1 className="text-2xl font-bold tracking-tight text-zinc-900" data-testid="sso-app-heading">
            Sign in to continue to <span className="text-indigo-600">{appName}</span>
          </h1>
        </div>

        <Card className="border-zinc-200 shadow-sm animate-fade-in" style={{ animationDelay: '0.1s' }}>
          {mfa ? (
            <>
              <CardHeader className="space-y-1 pb-4">
                <CardTitle className="text-xl">Two-factor authentication</CardTitle>
                <CardDescription>
                  {mfa.method === 'email'
                    ? `Enter the code sent to ${mfa.masked_email}`
                    : 'Enter the 6-digit code from your authenticator app'}
                </CardDescription>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleVerify2fa} className="space-y-4">
                  <Input
                    inputMode="numeric"
                    placeholder="123456"
                    value={otp}
                    onChange={(e) => setOtp(e.target.value)}
                    className="text-center tracking-[0.5em] text-lg"
                    required
                    data-testid="sso-2fa-input"
                  />
                  <Button type="submit" className="w-full bg-indigo-600 hover:bg-indigo-700" disabled={submitting} data-testid="sso-2fa-submit-btn">
                    {submitting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <ArrowRight className="w-4 h-4 mr-2" />}
                    Verify & continue
                  </Button>
                </form>
              </CardContent>
            </>
          ) : user && !redirecting ? (
            <>
              <CardHeader className="space-y-1 pb-4">
                <CardTitle className="text-xl">Continue as {user.name || user.email}</CardTitle>
                <CardDescription>You're signed in to PostToSocial as {user.email}</CardDescription>
              </CardHeader>
              <CardContent className="space-y-3">
                <Button
                  className="w-full bg-indigo-600 hover:bg-indigo-700"
                  onClick={grantConsentAndRedirect}
                  data-testid="sso-continue-btn"
                >
                  <ArrowRight className="w-4 h-4 mr-2" />
                  Continue to {appName}
                </Button>
                <Button
                  variant="ghost"
                  className="w-full text-zinc-500"
                  onClick={logout}
                  data-testid="sso-switch-account-btn"
                >
                  Not you? Use a different account
                </Button>
              </CardContent>
            </>
          ) : redirecting ? (
            <CardContent className="pt-8 pb-8 text-center space-y-3" data-testid="sso-redirecting">
              <Loader2 className="w-8 h-8 animate-spin text-indigo-600 mx-auto" />
              <p className="text-sm text-zinc-500">Redirecting you back to {appName}…</p>
            </CardContent>
          ) : (
            <>
              <CardHeader className="space-y-1 pb-4">
                <CardTitle className="text-xl">Sign in</CardTitle>
                <CardDescription>Use your PostToSocial account</CardDescription>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleLogin} className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="sso-email">Email</Label>
                    <div className="relative">
                      <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-400" />
                      <Input
                        id="sso-email"
                        type="email"
                        placeholder="name@example.com"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        className="pl-10"
                        required
                        data-testid="sso-email-input"
                      />
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="sso-password">Password</Label>
                    <div className="relative">
                      <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-400" />
                      <Input
                        id="sso-password"
                        type={showPassword ? 'text' : 'password'}
                        placeholder="••••••••"
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        className="pl-10 pr-10"
                        required
                        data-testid="sso-password-input"
                      />
                      <button
                        type="button"
                        onClick={() => setShowPassword((v) => !v)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-400 hover:text-zinc-600 transition-colors"
                        aria-label={showPassword ? 'Hide password' : 'Show password'}
                      >
                        {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                      </button>
                    </div>
                  </div>
                  <Button type="submit" className="w-full bg-indigo-600 hover:bg-indigo-700" disabled={submitting} data-testid="sso-submit-btn">
                    {submitting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <ArrowRight className="w-4 h-4 mr-2" />}
                    Sign in & continue
                  </Button>
                </form>

                <div className="relative my-5">
                  <div className="absolute inset-0 flex items-center">
                    <span className="w-full border-t border-zinc-200" />
                  </div>
                  <div className="relative flex justify-center text-xs">
                    <span className="bg-white px-3 text-zinc-400">or</span>
                  </div>
                </div>
                <GoogleAuthButton
                  label="Continue with Google"
                  ssoContext={{
                    client_id: clientId,
                    redirect_uri: redirectUri,
                    scope,
                    state,
                    code_challenge: codeChallenge,
                    code_challenge_method: codeChallengeMethod,
                  }}
                />
              </CardContent>
            </>
          )}
        </Card>

        <p className="text-center text-xs text-zinc-400 mt-6">
          Protected by PostToSocial SSO · You'll return to {appName} after signing in
        </p>
      </div>
    </div>
  );
};
